/**
 * countdown-timer.js
 * ---------------------------------------------------------------------------
 * Web component <countdown-timer> — contagem regressiva em dias/horas/min/seg
 * até a data em `expires-at` (formato ISO, ex: 2026-08-09T23:59:59-03:00).
 *
 * Atributos:
 *   expires-at            -> data/hora de expiração (ISO 8601)
 *   expiration-behavior    -> "hide" (remove a seção da página) ou "leave"
 *                             (para de contar e deixa os números parados)
 *
 * Espera 4 elementos filhos com [data-unit="days|hours|minutes|seconds"]
 * dentro do timer — o componente só atualiza o texto deles.
 * ---------------------------------------------------------------------------
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.units = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    const expiresAt = this.getAttribute('expires-at');
    if (!expiresAt) return;

    this.expirationDate = new Date(expiresAt);
    this._tick();
    this.interval = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  _tick() {
    const now = new Date();

    if (this.expirationDate < now) {
      clearInterval(this.interval);
      if (this.getAttribute('expiration-behavior') === 'hide') {
        this.closest('.shopify-section')?.remove();
      }
      return;
    }

    let delta = Math.abs(this.expirationDate - now) / 1000;

    const days = Math.floor(delta / 86400);
    delta -= days * 86400;
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;
    const minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;
    const seconds = Math.floor(delta % 60);

    this._updateUnit('days', days);
    this._updateUnit('hours', hours);
    this._updateUnit('minutes', minutes);
    this._updateUnit('seconds', seconds);
  }

  _updateUnit(name, value) {
    const el = this.units[name];
    if (!el) return;
    const formatted = Math.min(99, value).toString().padStart(2, '0');
    if (el.textContent === formatted) return;
    el.textContent = formatted;
    // pequeno pulso visual na troca do número (equivalente simplificado
    // ao efeito "flip" da Prestige, sem dependências externas)
    el.animate(
      [
        { transform: 'translateY(-4px)', opacity: 0.4 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: 220, easing: 'ease-out' }
    );
  }
}

if (!window.customElements.get('countdown-timer')) {
  window.customElements.define('countdown-timer', CountdownTimer);
}
